import { Transition } from '@headlessui/react'
import { createNodeInBetween, splitNode } from '@ui/lib/reactFlow/nodeMethods'
import { useEffect, useRef, useState } from 'react'
import { Edge, Node, NodeProps } from 'reactflow'

import { InBetweenIcon, PlusIcon, SplitIcon } from '@ui/components/icons'
import { NodeButtonAction, NodeButtonPosition, NodeButtonTrigger, NodeTypeEnum } from '@ui/interfaces'
import clsx from 'clsx'

interface NodeOptionButtonsProps {
  edges: Edge[]
  nodes: Node[]
  node: NodeProps
  handleClick: () => void
}

export const NodeOptionButtons = ({ node, nodes, edges, handleClick }: NodeOptionButtonsProps) => {
  const nodeContainerRef = useRef<HTMLDivElement>(null)
  const [showTopTrigger, setShowTopTrigger] = useState(false)
  const [showBottomButtons, setShowBottomButtons] = useState(false)
  const [showTopButtons, setShowTopButton] = useState(false)
  const [showBottomTrigger, setShowBottomTrigger] = useState(false)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      if (!target.closest('#top-trigger') && !target.closest('#bottom-trigger')) {
        setShowTopButton(false)
        setShowBottomButtons(false)
        setShowTopTrigger(false)
        setShowBottomTrigger(false)
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  const defaultIconStyles = 'h-2 w-2 cursor-pointer stroke-gray-600'

  const handleButtons = ({ action, position }: NodeButtonTrigger) => {
    if (position === NodeButtonPosition.LEFT) {
      const parentEdgeIds = edges.filter((edge) => edge.target === node.id).map((edge) => edge.id)
      if (parentEdgeIds.length > 0) {
        switch (action) {
          case NodeButtonAction.SPLIT:
            splitNode({ edgeIds: parentEdgeIds })
            break
          case NodeButtonAction.IN_BETWEEN:
            createNodeInBetween({ edgeIds: parentEdgeIds, position: NodeButtonPosition.LEFT })
            break
        }
      }

      return
    } else {
      const childEdgeIds = edges.filter((edge) => edge.source === node.id).map((edge) => edge.id)
      if (childEdgeIds.length > 0) {
        switch (action) {
          case NodeButtonAction.SPLIT:
            splitNode({ edgeIds: childEdgeIds })
            break
          case NodeButtonAction.IN_BETWEEN:
            createNodeInBetween({ edgeIds: childEdgeIds, position: NodeButtonPosition.RIGHT })
            break
        }
      }

      return
    }
  }

  const showTriggers = () => {
    if (nodeContainerRef.current) {
      const parentNodeId = edges.find((edge) => edge.target === node.id)
      const childNodeId = edges.find((edge) => edge.source === node.id)
      const parentNode = nodes.find((n) => n.id === parentNodeId?.source)
      const childNode = nodes.find((n) => n.id === childNodeId?.target)

      if (showTopButtons || showBottomButtons) {
        return
      }

      const isPlaceholder = (nodeType?: string) => nodeType === NodeTypeEnum.PLACEHOLDER
      const isFirehose = (nodeType?: string) => nodeType === NodeTypeEnum.FIREHOSE
      const isExport = (nodeType?: string) => nodeType === NodeTypeEnum.EXPORT

      const updateTriggers = (showTop: boolean, showBottom: boolean) => {
        setShowTopTrigger(showTop)
        setShowBottomTrigger(showBottom)
      }

      if ((isPlaceholder(parentNode?.type) && isPlaceholder(childNode?.type)) || isPlaceholder(node.type)) {
        updateTriggers(false, false)
      } else if (isFirehose(node.type) || isPlaceholder(parentNode?.type)) {
        updateTriggers(false, true)
      } else if (isPlaceholder(node.type) || isPlaceholder(childNode?.type) || isExport(node.type)) {
        updateTriggers(true, false)
      } else {
        updateTriggers(true, true)
      }
    }
  }

  const handleMouseLeave = () => {
    if (!showTopButtons) {
      setShowTopTrigger(false)
    }
    if (!showBottomButtons) {
      setShowBottomTrigger(false)
    }
  }

  const closeAllButtons = () => {
    setShowTopButton(false)
    setShowBottomButtons(false)
    setShowTopTrigger(false)
    setShowBottomTrigger(false)
  }

  return (
    <div
      className={clsx('absolute -left-0 -top-2 z-10 h-full w-full')}
      ref={nodeContainerRef}
      onMouseMove={showTriggers}
      onMouseLeave={handleMouseLeave}
      onClick={(e) => e.stopPropagation()}>
      <div className="absolute left-0 top-0 z-0 h-full w-full" onClick={handleClick} />
      {/* Top Button */}
      <Transition
        show={showTopTrigger}
        enter="transform transition-transform duration-300"
        enterFrom="-translate-y-3"
        enterTo="translate-y-0"
        leave="transform transition-transform duration-100"
        leaveFrom="translate-y-0"
        leaveTo="-translate-y-0">
        <div className="absolute left-[6.25rem] top-[-0.75rem]">
          <div
            id="top-trigger"
            data-testid="top-trigger"
            onClick={() => {
              setShowTopButton(!showTopButtons)
              setShowBottomButtons(false)
              setShowBottomTrigger(false)
            }}
            className={clsx(
              'absolute z-10 flex items-center justify-center',
              'rounded-full bg-white p-1.5 shadow-md transition duration-150',
              'hover:shadow-lg hover:ring-1 hover:ring-secondary-500 hover:ease-in-out',
              {
                'ring-1 ring-secondary-500': showTopButtons,
              }
            )}>
            <PlusIcon className={defaultIconStyles} />
          </div>

          <>
            <Transition
              show={showTopButtons}
              enter="opacity transition-opacity duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="opacity transition-opacity duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <div className="relative bottom-24 right-32 z-30 h-auto rounded-md border border-gray-400 bg-white">
                <div className="cursor-default rounded-t-md border-b border-gray-200 bg-gray-50">
                  <h2 className="p-2.5 text-xs font-medium text-gray-600">Create new nodes</h2>
                </div>
                <div
                  className="flex cursor-pointer items-center space-x-2 border-b border-gray-200 px-2.5 py-2 hover:bg-gray-100"
                  onClick={() => {
                    handleButtons({ action: NodeButtonAction.SPLIT, position: NodeButtonPosition.LEFT })
                    closeAllButtons()
                  }}>
                  <SplitIcon className="h-3 w-3 fill-white stroke-gray-500" />
                  <span className="text-[11px] font-normal text-gray-600">Split node</span>
                </div>
                <div
                  className="flex cursor-pointer items-center space-x-2 rounded-b-md border-b border-gray-200 px-2.5 py-2 hover:bg-gray-100"
                  onClick={() => {
                    handleButtons({ action: NodeButtonAction.IN_BETWEEN, position: NodeButtonPosition.LEFT })
                    closeAllButtons()
                  }}>
                  <InBetweenIcon className="h-3 w-3 fill-gray-500" />
                  <span className="text-[11px] font-normal text-gray-600">Add node</span>
                </div>
              </div>
            </Transition>
          </>
        </div>
      </Transition>

      {/* Right Buttons */}
      <Transition
        show={showBottomTrigger}
        enter="transform transition-transform duration-300"
        enterFrom="translate-y-3"
        enterTo="translate-x-0"
        leave="transform transition-transform duration-0"
        leaveFrom="translate-x-0"
        leaveTo="-translate-y-0">
        <div className="absolute -bottom-2 right-[7.5rem]">
          <div
            id="bottom-trigger"
            data-testid="bottom-trigger"
            onClick={() => {
              setShowBottomButtons(!showBottomButtons)
            }}
            className={clsx(
              'relative right-0 z-10 flex items-center justify-center',
              'rounded-full bg-white p-1.5 shadow-md transition duration-150',
              'hover:shadow-lg hover:ring-1 hover:ring-secondary-500 hover:ease-in-out',
              {
                'ring-1 ring-secondary-500': showBottomButtons,
              }
            )}>
            <PlusIcon className={defaultIconStyles} />
          </div>

          <>
            <Transition
              show={showBottomButtons}
              enter="opacity transition-opacity duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="opacity transition-opacity duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <div className="absolute -bottom-24 -right-32 z-30 h-auto rounded-md border border-gray-400 bg-white">
                <div className="cursor-default rounded-t-md border-b border-gray-200 bg-gray-50">
                  <h2 className="p-2.5 text-xs font-medium text-gray-600">Create new nodes</h2>
                </div>
                <div
                  className="flex cursor-pointer items-center space-x-2 border-b border-gray-200 px-2.5 py-2 hover:bg-gray-100"
                  onClick={() => {
                    closeAllButtons()
                    handleButtons({ action: NodeButtonAction.SPLIT, position: NodeButtonPosition.RIGHT })
                  }}>
                  <SplitIcon className="h-3 w-3 fill-white stroke-gray-500" />
                  <span className="text-[11px] font-normal text-gray-600">Split node</span>
                </div>
                <div
                  className="flex cursor-pointer items-center space-x-2 rounded-b-md border-b border-gray-200 px-2.5 py-2 hover:bg-gray-100"
                  onClick={() => {
                    handleButtons({ action: NodeButtonAction.IN_BETWEEN, position: NodeButtonPosition.RIGHT })
                    closeAllButtons()
                  }}>
                  <InBetweenIcon className="h-3 w-3 fill-gray-500" />
                  <span className="text-[11px] font-normal text-gray-600">Add node</span>
                </div>
              </div>
            </Transition>
          </>
        </div>
      </Transition>
    </div>
  )
}
